// Lightweight inline-SVG primitives. We deliberately avoid recharts here so
// the admin bundle stays small; once the dataset grows we can swap to a real
// chart lib without touching call sites.

import { useMemo } from 'react';

type DonutSlice = { label: string; value: number; color: string };

export function Donut({
  slices,
  size = 160,
  thickness = 26,
  centerLabel,
  centerSub,
}: {
  slices: readonly DonutSlice[];
  size?: number;
  thickness?: number;
  centerLabel?: string;
  centerSub?: string;
}) {
  const total = slices.reduce((s, x) => s + x.value, 0) || 1;
  const r = size / 2 - thickness / 2 - 2;
  const C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
        <g transform={`translate(${size / 2} ${size / 2}) rotate(-90)`}>
          {slices.map((s) => {
            const len = (s.value / total) * C;
            const dash = `${len} ${C - len}`;
            const el = (
              <circle
                key={s.label}
                r={r}
                fill="transparent"
                stroke={s.color}
                strokeWidth={thickness}
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += len;
            return el;
          })}
        </g>
        {(centerLabel || centerSub) && (
          <g>
            {centerLabel && (
              <text
                x={size / 2}
                y={size / 2 - (centerSub ? 4 : -4)}
                textAnchor="middle"
                fontSize="22"
                fontWeight="600"
                fill="var(--fg-1)"
                fontFamily="Inter"
              >
                {centerLabel}
              </text>
            )}
            {centerSub && (
              <text
                x={size / 2}
                y={size / 2 + 16}
                textAnchor="middle"
                fontSize="11"
                fill="var(--fg-3)"
                fontFamily="Inter"
              >
                {centerSub}
              </text>
            )}
          </g>
        )}
      </svg>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6, font: '500 12px/16px var(--font-body)', color: 'var(--fg-2)' }}>
        {slices.map((s) => (
          <li key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} aria-hidden="true" />
            <span>{s.label}</span>
            <span className="mono" style={{ marginLeft: 'auto', color: 'var(--fg-1)' }}>
              {((s.value / total) * 100).toFixed(1).replace('.', ',')}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function HBars({
  rows,
  max,
  format = (n) => n.toString(),
}: {
  rows: ReadonlyArray<{ label: string; value: number; sub?: string; color?: string }>;
  max?: number;
  format?: (n: number) => string;
}) {
  const m = max ?? Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((r) => (
        <div
          key={r.label}
          style={{ display: 'grid', gridTemplateColumns: '160px 1fr 80px', alignItems: 'center', gap: 12 }}
        >
          <div style={{ font: '500 13px/18px var(--font-body)', color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.label}
            {r.sub && <span style={{ color: 'var(--fg-3)', font: '500 11px/15px var(--font-mono)', marginLeft: 6 }}>{r.sub}</span>}
          </div>
          <span
            style={{
              background: '#F2F4F6',
              borderRadius: 9999,
              height: 10,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${Math.max(2, (r.value / m) * 100).toFixed(1)}%`,
                background: r.color ?? 'var(--brand-blue)',
                borderRadius: 9999,
              }}
            />
          </span>
          <span className="mono" style={{ textAlign: 'right', color: 'var(--fg-2)' }}>{format(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function Heatmap({
  rows,
  cols,
  values,
  format = (v) => `${(v * 100).toFixed(0)}%`,
}: {
  rows: readonly string[];
  cols: readonly string[];
  values: number[][]; // [row][col] in [0..1]
  format?: (v: number) => string;
}) {
  return (
    <div style={{ overflow: 'auto' }}>
      <table style={{ borderCollapse: 'separate', borderSpacing: 4, width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: 120 }} />
            {cols.map((c) => (
              <th
                key={c}
                style={{ font: '500 10px/14px var(--font-mono)', color: 'var(--fg-3)', letterSpacing: '0.5px', textTransform: 'uppercase', textAlign: 'center', paddingBottom: 4 }}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rowLabel, ri) => (
            <tr key={rowLabel}>
              <th style={{ font: '500 13px/18px var(--font-body)', color: 'var(--fg-1)', textAlign: 'left', padding: '0 8px', whiteSpace: 'nowrap' }}>
                {rowLabel}
              </th>
              {cols.map((_, ci) => {
                const v = values[ri]?.[ci] ?? 0;
                return (
                  <td
                    key={ci}
                    title={format(v)}
                    style={{
                      height: 28,
                      borderRadius: 6,
                      background: heatColor(v),
                      color: v > 0.55 ? '#FFFFFF' : 'var(--fg-1)',
                      font: '600 11px/16px var(--font-mono)',
                      textAlign: 'center',
                      transition: 'transform 120ms',
                    }}
                  >
                    {format(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function heatColor(v: number): string {
  // Cool→hot scale aligned with brand blue→cyan→green→orange→red.
  const t = Math.max(0, Math.min(1, v));
  if (t < 0.2) return `rgba(42, 135, 177, ${0.10 + t})`;
  if (t < 0.4) return `rgba(60, 180, 165, ${0.25 + t * 0.5})`;
  if (t < 0.6) return `rgba(71, 185, 124, ${0.4 + t * 0.4})`;
  if (t < 0.8) return `rgba(238, 136, 45, ${0.5 + t * 0.3})`;
  return `rgba(231, 87, 50, ${0.6 + t * 0.4})`;
}

export function Bullet({
  label,
  value,
  target,
  max = 5,
  goodAbove,
  format = (n) => n.toFixed(1).replace('.', ','),
}: {
  label: string;
  value: number;
  target: number;
  max?: number;
  goodAbove?: boolean;
  format?: (n: number) => string;
}) {
  const good = goodAbove ?? true;
  const meets = good ? value >= target : value <= target;
  const color = meets ? 'var(--brand-green)' : 'var(--brand-red)';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 70px', alignItems: 'center', gap: 12 }}>
      <div style={{ font: '500 13px/18px var(--font-body)', color: 'var(--fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </div>
      <div style={{ position: 'relative', background: '#F2F4F6', borderRadius: 9999, height: 10, overflow: 'hidden' }}>
        <span
          style={{
            display: 'block',
            height: '100%',
            width: `${Math.max(2, (value / max) * 100)}%`,
            background: color,
            borderRadius: 9999,
          }}
        />
        <span
          style={{
            position: 'absolute',
            top: -2,
            bottom: -2,
            left: `${(target / max) * 100}%`,
            width: 2,
            background: 'var(--fg-1)',
          }}
          aria-hidden="true"
        />
      </div>
      <span className="mono" style={{ textAlign: 'right', color: 'var(--fg-2)' }}>{format(value)}</span>
    </div>
  );
}

export function MultiSelectChip<T extends string>({
  label,
  options,
  selected,
  onChange,
  icon,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  selected: readonly T[];
  onChange: (next: T[]) => void;
  icon?: string;
}) {
  // Inline single-select chip group; keeps the toolbar simple without modals.
  return (
    <div className="seg" role="group" aria-label={label}>
      {icon && (
        <span
          className="material-symbols-rounded"
          style={{ fontSize: 16, padding: '0 6px 0 10px', color: 'var(--fg-3)', display: 'inline-flex', alignItems: 'center' }}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
      {options.map((o) => {
        const on = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={on ? 'on' : ''}
            onClick={() => {
              if (on) onChange(selected.filter((v) => v !== o.value));
              else onChange([...selected, o.value]);
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function useFilteredReviews<R extends {
  daysAgo: number;
  productGroup: string;
  season: string;
  rating: number;
  channel: string;
  status: string;
  loyalty: boolean;
}>(
  reviews: readonly R[],
  filters: {
    range: '7d' | '30d' | '90d' | 'ytd' | '12m' | 'all';
    groups: readonly string[];
    seasons: readonly string[];
    stars: readonly number[];
    channels: readonly string[];
    statuses: readonly string[];
    loyalty: 'all' | 'on' | 'off';
  },
): readonly R[] {
  return useMemo(() => {
    const cutoff =
      filters.range === '7d' ? 7 :
      filters.range === '30d' ? 30 :
      filters.range === '90d' ? 90 :
      filters.range === 'ytd' ? new Date().getMonth() * 30 + new Date().getDate() :
      filters.range === '12m' ? 365 :
      Number.POSITIVE_INFINITY;
    return reviews.filter((r) => {
      if (r.daysAgo > cutoff) return false;
      if (filters.groups.length > 0 && !filters.groups.includes(r.productGroup)) return false;
      if (filters.seasons.length > 0 && !filters.seasons.includes(r.season)) return false;
      if (filters.stars.length > 0 && !filters.stars.includes(r.rating)) return false;
      if (filters.channels.length > 0 && !filters.channels.includes(r.channel)) return false;
      if (filters.statuses.length > 0 && !filters.statuses.includes(r.status)) return false;
      if (filters.loyalty === 'on' && !r.loyalty) return false;
      if (filters.loyalty === 'off' && r.loyalty) return false;
      return true;
    });
  }, [reviews, filters]);
}
